import React from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiTrendingUp, FiTarget, FiAward } from 'react-icons/fi';
import '../styles/Pages.css';
import EnhancedHeader from '../components/EnhancedHeader';

const Partners = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const partnerCategories = [
    { icon: FiUsers, name: 'Local Authorities', desc: 'Primary commissioning partners for placements' },
    { icon: FiTarget, name: 'Healthcare Services', desc: 'Hospital and A/E ward referrals' },
    { icon: FiUsers, name: 'Social Services', desc: 'Coordinated care delivery partners' },
    { icon: FiTrendingUp, name: 'Mental Health Providers', desc: 'Therapeutic support specialists' },
    { icon: FiAward, name: 'Care Agencies', desc: 'Quality oversight and regulation' },
    { icon: FiUsers, name: 'Community Resources', desc: 'Integration and support services' }
  ];

  return (
    <div className="page-container">
      <EnhancedHeader
        title="Our Partners"
        subtitle="Working together for better outcomes"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(230, 126, 34, 0.7) 0%, rgba(209, 97, 33, 0.7) 100%)"
      />


      {/* Partners Overview Section */}
      <motion.section
        className="partners-overview-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <motion.div
            className="partners-intro"
            variants={itemVariants}
          >
            <h2 className="section-title">Strategic Partnerships</h2>
            <p className="section-text">
              Active Care Low (Registration: 14464980) works in collaboration with a diverse network of organizations and agencies to provide comprehensive support and services to vulnerable adults. Our partnerships are built on shared values of quality care, professional excellence, and resident wellbeing.
            </p>
          </motion.div>

          <div className="partners-grid-section">
            <div className="partners-grid">
              {partnerCategories.map((partner, i) => (
                <motion.div
                  key={i}
                  className="partner-card-large"
                  variants={itemVariants}
                  whileHover={{
                    y: -15,
                    boxShadow: '0 20px 50px rgba(52, 152, 219, 0.2)'
                  }}
                >
                  <partner.icon className="partner-icon" />
                  <h3>{partner.name}</h3>
                  <p>{partner.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="partners-image">
              {/* IMAGE PLACEMENT 1: Partnership/Collaboration */}
              <img src="/images/acr-partners.jpg" alt="Partnership" />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Partnership Benefits Section */}
      <motion.section
        className="benefits-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Benefits of Partnership</h2>

          <div className="benefits-grid">
            {[
              'Coordinated care delivery and information sharing',
              'Professional networking and relationship building',
              'Quality assurance and regulatory compliance',
              'Access to specialist services and therapeutic support',
              'Collaborative approach to achieving outstanding outcomes',
              'Regular communication and progress reviews'
            ].map((benefit, i) => (
              <motion.div
                key={i}
                className="benefit-item"
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: { opacity: 1, x: 0 }
                }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ x: 10, backgroundColor: 'rgba(52, 152, 219, 0.05)' }}
              >
                <span className="benefit-check">✓</span>
                <span>{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Services Through Partnerships with Image 2 */}
      <motion.section
        className="section-split reverse"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="split-grid">
            <div className="split-image">
              {/* IMAGE PLACEMENT 2: Services/Support */}
              <img src="/images/infographic.png" alt="Services Through Partnerships" />
            </div>

            <motion.div
              className="split-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Services Provided Through Partnerships</h2>

              <div className="services-list">
                {[
                  'Professional therapeutic input and counseling services',
                  'Mental health assessment and intervention',
                  'Educational support and training opportunities',
                  'Employment pathways and job placement assistance',
                  'Life skills training and personal development',
                  'Crisis intervention and emergency support',
                  'Safeguarding and welfare oversight',
                  'Community integration and social activities'
                ].map((service, i) => (
                  <motion.div
                    key={i}
                    className="service-list-item"
                    variants={itemVariants}
                    whileHover={{ x: 10 }}
                  >
                    <span className="service-bullet">→</span> {service}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Partnership Commitment Section */}
      <motion.section
        className="commitment-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Our Commitment to Partners</h2>

          <div className="commitment-grid">
            {[
              { title: 'Quality', desc: 'Delivering outstanding care that meets UK regulatory standards' },
              { title: 'Communication', desc: 'Maintaining regular contact and transparent information sharing' },
              { title: 'Responsiveness', desc: 'Addressing partner and resident needs promptly and effectively' },
              { title: 'Accountability', desc: 'Taking responsibility for outcomes and continuous improvement' },
              { title: 'Collaboration', desc: 'Working together to achieve positive resident outcomes' },
              { title: 'Transparency', desc: 'Open reporting and shared decision-making processes' }
            ].map((commitment, i) => (
              <motion.div
                key={i}
                className="commitment-item"
                variants={itemVariants}
                whileHover={{
                  y: -10,
                  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)'
                }}
              >
                <h3>{commitment.title}</h3>
                <p>{commitment.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Become a Partner CTA */}
      <motion.section
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2>Interested in Partnership?</h2>
          <p>Contact us today to discuss collaboration opportunities</p>

          <motion.button
            className="btn btn-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get in Touch
          </motion.button>
        </div>
      </motion.section>
    </div>
  );
};

export default Partners;
